/**
 * Edit Game — /(app)/games/[id]/setup
 *
 * Host-only LOBBY screen: the setup page in edit mode. Every change (seats,
 * auto-start, visibility and boot timer included) stays local until Save,
 * which PATCHes `/settings` and `/setup` with just what changed and returns to
 * the lobby. Discard resets the working copy.
 *
 * `section=map` opens the map picker, whose Done saves and returns;
 * `section=rules` scrolls to the Rules card.
 */

import { teamsConfigOf } from '@warcarp/api-types'
import React, { useState } from 'react'
import { Text, ActivityIndicator, View } from 'react-native'
import { useLocalSearchParams, useRouter } from 'expo-router'
import { SafeAreaView } from 'react-native-safe-area-context'
import { useGame, useUpdateGameSetup, useUpdateGameSettings } from '../../../../hooks/useGames'
import { useMaps, useMap } from '../../../../hooks/useMaps'
import { useAuth } from '../../../../hooks/useAuth'
import { useTheme } from '../../../../hooks/useTheme'
import { useToast } from '../../../../components/ui/Toast'
import { Button } from '../../../../components/ui/Button'
import { mapPickerOptions } from '../../../../lib/mapPickerOptions'
import { useReopenOnFocus } from '../../../../hooks/useReopenOnFocus'
import { GameSetupPage } from '../../../../components/game/setup/GameSetupPage'
import { draftFromGame, lobbySeats, setupPatches, type GameSetupDraft } from '../../../../lib/gameSetupDraft'
import { teamRegroupMoves } from '../../../../lib/teamSetup'
import { LobbyPopup } from '../../../../components/game/lobby/LobbyPopup'

export default function EditGameSetupScreen() {
  const { id, section } = useLocalSearchParams<{ id: string; section?: string }>()
  const router = useRouter()
  const { user } = useAuth()
  const { showToast } = useToast()
  const { theme } = useTheme()
  const { data: game, isLoading } = useGame(id)
  const { data: mapSummaries } = useMaps()
  const updateSetup = useUpdateGameSetup(id ?? '')
  const updateSettings = useUpdateGameSettings(id ?? '')

  const availableMaps = React.useMemo(() => mapPickerOptions(mapSummaries), [mapSummaries])

  // The saved values (follow the server), and the local working copy seeded
  // from them once. Edits live in `draft` until Save.
  const base = React.useMemo(() => (game ? draftFromGame(game) : null), [game])
  const [draft, setDraft] = useState<GameSetupDraft | null>(null)
  React.useEffect(() => {
    if (base && draft === null) setDraft(base)
  }, [base, draft])
  const [valid, setValid] = useState(true)
  const [saving, setSaving] = useState(false)
  // A team refusal (format, bots, seats) shows inline above Save.
  const [error, setError] = useState<string | null>(null)
  // Pick seats regrouped by a new size: confirm who changes team first.
  const [regroup, setRegroup] = useState<string[] | null>(null)
  const onDraftChange = React.useCallback((next: GameSetupDraft) => { setDraft(next); setError(null) }, [])

  const [mapPickerOpen, setMapPickerOpen] = useState(section === 'map')
  const reopenMapPicker = useReopenOnFocus(setMapPickerOpen)
  React.useEffect(() => { setMapPickerOpen(section === 'map') }, [section])

  const { data: selectedMap } = useMap(draft?.mapId)
  // The map the game has now: PATCH /settings checks seats against it.
  const { data: savedMap } = useMap(game?.mapId ?? undefined)

  if (isLoading || !game || !draft || !base) {
    return (
      <SafeAreaView className="flex-1 bg-canvas items-center justify-center">
        <ActivityIndicator color="#ef4444" accessibilityLabel="Loading game" />
      </SafeAreaView>
    )
  }

  const isHost = game.adminId === user?.id
  if (!isHost || game.status !== 'LOBBY') {
    // Defensive — the lobby CTA only renders for the host, before the start,
    // but a direct URL probe should land somewhere sensible.
    return (
      <SafeAreaView className="flex-1 bg-canvas items-center justify-center gap-4 px-6">
        <Text className="text-ink font-stencil text-lg uppercase">{isHost ? 'Game has started' : 'Host only'}</Text>
        {isHost && <Text className="text-ink-soft font-typed text-sm text-center">Setup can't be changed after the game begins.</Text>}
        <Button title="Back to game" variant="secondary" onPress={() => router.replace(`/(app)/games/${id}`)} />
      </SafeAreaView>
    )
  }

  const seats = lobbySeats(game)
  const currentBase = base
  const currentDraft = draft
  const seatedPlayers = game.players.length

  const teamsInvolved = !!teamsConfigOf(currentBase.config) || !!teamsConfigOf(currentDraft.config)
  const moves = teamRegroupMoves(game.players, teamsConfigOf(currentBase.config), teamsConfigOf(currentDraft.config))

  function requestSave() {
    if (valid && moves.length > 0) {
      setRegroup(moves)
      return
    }
    void handleSave()
  }

  async function handleSave() {
    setRegroup(null)
    if (!valid) {
      showToast('Some rules are invalid', 'error')
      return
    }
    setError(null)
    const { settings, setup, settingsFirst } = setupPatches(currentBase, currentDraft, savedMap, seatedPlayers)
    const saveSettings = async () => { if (settings) await updateSettings.mutateAsync(settings) }
    const saveSetup = async () => { if (setup) await updateSetup.mutateAsync(setup) }
    setSaving(true)
    try {
      if (settingsFirst) { await saveSettings(); await saveSetup() } else { await saveSetup(); await saveSettings() }
      router.back()
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Could not save changes'
      if (teamsInvolved) setError(message)
      else showToast(message, 'error')
    } finally {
      setSaving(false)
    }
  }

  const pending = setupPatches(base, draft)
  const hasChanges = pending.settings !== null || pending.setup !== null

  const baseTeams = teamsConfigOf(base.config)
  const draftTeams = teamsConfigOf(draft.config)
  return (
    <>
    <GameSetupPage
      mode="edit"
      heading="Edit Game"
      backLabel="Back to lobby"
      onBack={() => router.back()}
      meta={`${game.name || 'Untitled'} · waiting for players (${seats.seated}/${game.maxPlayers})`}
      draft={draft}
      onChange={onDraftChange}
      error={error}
      base={base}
      map={selectedMap}
      maps={availableMaps}
      titlePlaceholder="Name this game (optional)"
      titleMaxLength={80}
      lobby={{ floor: seats.floor, seated: seats.seated }}
      mapPicker={{
        open: mapPickerOpen,
        onOpenChange: setMapPickerOpen,
        onInspectMap: (mapId) => {
          reopenMapPicker()
          setMapPickerOpen(false)
          router.push(`/(app)/maps/${mapId}`)
        },
        // Entered from the lobby's "Edit map": Done saves the choice and
        // returns to the lobby rather than leaving the host on Edit Game.
        ...(section === 'map' ? { onDone: requestSave, doneLabel: 'Done — save and back to lobby' } : {}),
      }}
      focusRules={section === 'rules'}
      onValidityChange={setValid}
      primary={{ label: 'Save changes', onPress: requestSave, loading: saving, disabled: !hasChanges }}
      onDiscard={() => { setDraft(base); setError(null) }}
    />
    {/* Pick seats: a new team size regroups the seats (teams-config.md "Editing in the lobby"). */}
    <LobbyPopup visible={regroup !== null} title="Save changes?" closeLabel="Close save confirmation" onClose={() => setRegroup(null)} testID="team-regroup-modal" maxWidth={420}>
      <View style={{ gap: 10 }}>
        {baseTeams && draftTeams && <Text className="font-typed text-ink text-sm">
          Teams of {baseTeams.size} → teams of {draftTeams.size} ({draft.maxPlayers} seats). Seats are regrouped:
        </Text>}
        {(regroup ?? []).map((line) => <Text key={line} className="font-typed italic text-xs" style={{ color: theme === 'dark' ? '#fbbf24' : '#b45309' }}>{line}</Text>)}
        <View className="flex-row" style={{ gap: 10, marginTop: 4 }}>
          <View style={{ flex: 1 }}><Button title="Cancel" variant="secondary" onPress={() => setRegroup(null)} accessibilityLabel="Cancel saving" /></View>
          <View style={{ flex: 2 }}><Button title="Save" onPress={() => { void handleSave() }} loading={saving} accessibilityLabel="Save and regroup" /></View>
        </View>
      </View>
    </LobbyPopup>
    </>
  )
}
